'use client';

import { BuilderStore, useIsPreviewing } from '@builder.io/react';
import * as blotout from 'components/analytics/blotout';
import * as gtm from 'components/analytics/gtm';
import useUser from 'components/hooks/use-user';
import { withSuspense } from 'components/with-suspense';
import { PlaceOTOOrderResponse } from 'lib/rest-service/oto-service';
import { createUrl, hasPaymentPlan } from 'lib/utils';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, {
  ButtonHTMLAttributes,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useFormStatus } from 'react-dom';
import * as customAnalytics from '../analytics/custom';
import ButtonArrow from '../icons/button-arrow';
import CheckRect from '../icons/check-rect';
import CheckBoxChecked from '../icons/checkbox-checked';
import Vector from '../icons/vector';
import * as Intellisuggest from '../ss-analytics/intellisuggest';
import TilaModal from '../tila/tila-modal';
import CreateAccountModal from './create-account-modal';
import SignInModal from './sign-in-modal';

import useCustomAnalyticsState from 'components/hooks/use-custom-analytics-state';
import useProduct from 'components/hooks/use-product';
import { useFunnelData } from 'components/providers/funnel-data-provider';
import { getCookie } from 'cookies-next';
import './click-to-purchase-cta.css';

function getClassName(buttonColor: string, buttonSize: string, buttonEmbellish: string) {
  let isFancy = false;
  let buttonClass = '';
  switch (buttonColor) {
    case 'Text':
      buttonClass = 'cta-button-text cta-button-text-atc cql-formatted-text';
      break;
    case 'Primary':
      buttonClass = 'cta-button cta-button-atc';
      break;
    case 'Primary Inverted':
      buttonClass = 'cta-button cta-button-inverted';
      break;
    case 'Secondary':
      buttonClass = 'cta-button cta-button-secondary';
      break;
    case 'Secondary Inverted':
      buttonClass = 'cta-button cta-button-secondary-inverted';
      break;
    case 'ATC Tile':
      buttonClass = 'tile__atc';
      break;
    case '4P Orange 1':
      buttonClass = 'cta-button cta-4p-button cta-4p-button-orange1';
      isFancy = true;
      break;
    case '4P Orange 2':
      buttonClass = 'cta-button cta-4p-button cta-4p-button-orange2';
      isFancy = true;
      break;
    case '4P Green':
      buttonClass = 'cta-button cta-4p-button cta-4p-button-green';
      isFancy = true;
      break;
  }
  if (isFancy) {
    buttonClass += ` cta-4p-button-${buttonSize.toLowerCase()}`;
    if (buttonEmbellish !== 'None')
      buttonClass += ` cta-4p-button-embellish-${buttonEmbellish.toLowerCase()}`;
  }

  return buttonClass;
}

function getIcon(purchasedIcon: 'Check' | 'Check Rect' | 'Check Rect Rounded' | 'Checkbox') {
  switch (purchasedIcon) {
    case 'Check':
      return <Vector />;
    case 'Check Rect':
      return <CheckRect />;
    case 'Check Rect Rounded':
      return <CheckRect className="rounded" />;
    case 'Checkbox':
      return <CheckBoxChecked />;
    default:
      return null;
  }
}

type PurchasedIconType = 'Check' | 'Check Rect' | 'Check Rect Rounded' | 'Checkbox';

interface CTAButtonProps {
  buttonColor: string;
  buttonSize: string;
  buttonEmbellish: string;
  buttonFontSize: number | undefined;
  buttonText?: string;
  buttonHTML?: string;
  loading: boolean;
  disabled?: boolean;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  onSubmit?: () => void;
}

export const CTAButton: React.FC<CTAButtonProps> = ({
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonFontSize,
  buttonText,
  buttonHTML,
  loading,
  disabled,
  type,
  onSubmit
}) => {
  const { pending } = useFormStatus();
  const isLoading = loading || pending;
  const buttonStyle =
    buttonFontSize && buttonFontSize > 0 ? { fontSize: `${buttonFontSize}px` } : {};

  let embelishStyle = '';
  if (buttonColor.match(/(^4P.+)/))
    embelishStyle = `cta-4p-button-embellish-${buttonColor
      .replace(/(^4P)/, '')
      .replace(/(\s)/g, '')
      .toLowerCase()}`;

  const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (isLoading) return e.preventDefault();
    if (onSubmit) onSubmit();
  };

  let cleanHTML = '';
  if (buttonColor === 'Text') {
    const rawHTML = buttonHTML || buttonText || 'Click to Purchase';
    cleanHTML = rawHTML
      .replace('<p', '<span')
      .replace('</p', '</span')
      .replace('<div', '<span')
      .replace('</div', '</span');
  }

  return (
    <>
      {embelishStyle && buttonEmbellish === 'Circle' && (
        <img
          src="https://cdn.builder.io/api/v1/image/assets%2F1adbd5971dac4273891f273603729777%2Fab43a3fc60de4a7aa41dde3b4c17d3ac"
          className={`cta-4p-button-embellish cta-4p-button-embellish-circle ${embelishStyle}`}
          alt=""
        />
      )}
      {embelishStyle && buttonEmbellish === 'Arrows' && (
        <span
          className={`cta-4p-button-embellish cta-4p-button-embellish-arrow cta-4p-button-embellish-arrow-left ${embelishStyle}`}
        >
          <ButtonArrow />
        </span>
      )}
      <button
        aria-label={buttonText}
        className={getClassName(buttonColor, buttonSize, buttonEmbellish)}
        style={buttonStyle}
        onClick={onClick}
        disabled={disabled}
        type={type}
      >
        {buttonColor === 'Text' ? (
          <span
            dangerouslySetInnerHTML={{
              __html: cleanHTML
            }}
          ></span>
        ) : (
          buttonText
        )}
        {isLoading && <div className="loadingOverlay" />}
      </button>
      {embelishStyle && buttonEmbellish === 'Arrows' && (
        <span
          className={`cta-4p-button-embellish cta-4p-button-embellish-arrow cta-4p-button-embellish-arrow-right ${embelishStyle}`}
        >
          <ButtonArrow />
        </span>
      )}
    </>
  );
};

interface PurchasedProps {
  purchasedIcon: PurchasedIconType;
  purchasedText: string;
  purchasedFontSize: number | undefined;
  setPurchased?: (purchased: boolean) => void;
}

const Purchased: React.FC<PurchasedProps> = ({
  purchasedIcon,
  purchasedText,
  purchasedFontSize,
  setPurchased
}) => {
  const purchasedStyle =
    purchasedFontSize && purchasedFontSize > 0 ? { fontSize: `${purchasedFontSize}px` } : {};

  return (
    <div className="purchased" onClick={setPurchased && (() => setPurchased(false))}>
      {getIcon(purchasedIcon)}
      <p style={purchasedStyle}>{purchasedText}</p>
    </div>
  );
};

interface ClickToPurchaseCTAProps {
  builderState?: BuilderStore;
  attributes: {
    style: React.CSSProperties;
    className: string;
    'builder-id': string;
    id: string;
  };
  variantSku: string;
  buttonColor: string;
  buttonSize: string;
  buttonEmbellish: string;
  buttonText: string;
  buttonHTML: string;
  buttonFontSize: number | undefined;
  purchasedIcon: PurchasedIconType;
  purchasedText: string;
  purchasedFontSize: number | undefined;
  onSuccessUrl?: string;
  onExpireUrl?: string;
}

export const ClickToPurchaseCTAComponent: React.FC<ClickToPurchaseCTAProps> = ({
  builderState,
  attributes,
  variantSku,
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonText,
  buttonHTML,
  buttonFontSize,
  purchasedIcon,
  purchasedText,
  purchasedFontSize,
  onSuccessUrl,
  onExpireUrl
}) => {
  const [loading, setLoading] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [error, setError] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(false);
  const [needConfirm, setNeedConfirm] = useState(false);
  const [showTila, setShowTila] = useState(false);
  const { product } = useProduct({ sku: variantSku });
  const router = useRouter();
  const isEditing = useIsPreviewing();
  const { user, loggedIn, mutate } = useUser();

  const revalidateUser = useCallback(() => {
    mutate();
  }, [mutate]);

  const pathname = usePathname() || '';
  const searchParams = useSearchParams();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const ctaButtonRef = useRef<HTMLDivElement | null>(null);
  const purchasedRef = useRef<HTMLDivElement | null>(null);

  const funnelData = useFunnelData();
  const { state } = useCustomAnalyticsState();
  const [, _offers, productLineUrl, funnelNameUrl, _offer, _offerName] = pathname.split('/');
  const productLine: string =
    builderState?.state?.productLine ?? funnelData.productLine ?? productLineUrl ?? '';
  const funnelName: string =
    builderState?.state?.funnelName ?? funnelData.funnelName ?? funnelNameUrl ?? '';
  const acceptOfferUrl = onSuccessUrl ?? pathname.replace(/offer\/.*/, 'thank-you');
  const returnToOfferUrl = pathname;
  const stepName = funnelData.stepName ?? '';
  const type = funnelData.type ?? '';

  let isGuest = !loggedIn || (user?.isGuest ?? true);
  // If they cancelled the Sign-In to Purchase flow, treat them as a guest.
  if (searchParams.get('cancelSignInToPurchase')) {
    isGuest = true;
  }
  const isNewGuest = isGuest && (user?.isNewGuest ?? false);

  useEffect(() => {
    const paymentPlan = hasPaymentPlan(product, variantSku);
    setPaymentPlan(paymentPlan);
    if (paymentPlan) setNeedConfirm(true);
  }, [variantSku, product]);

  const handleAfterClose = () => {
    const submitButton = document.querySelector('#checkout-payment-continue');
    if (submitButton) {
      (submitButton as HTMLButtonElement).blur();
    }
  };

  const handleConfirm = () => {
    setNeedConfirm(false);
    return onSubmit(true);
  };

  const onSubmit = async (isConfirmed: Boolean = false) => {
    if (paymentPlan && isGuest) {
      return setShowAuthModal(true);
    }
    if (paymentPlan && needConfirm && !isConfirmed) {
      return setShowTila(true);
    }

    if (isEditing) {
      setLoading(true);
      setTimeout(() => {
        setPurchased(true);
        setLoading(false);
      }, 1000);
      return;
    }

    try {
      setLoading(true);
      const result = await placeOTOOrder(variantSku);
      if (result?.success) {
        setPurchased(true);
        try {
          const customAnalyticsPromise = customAnalytics.purchase({
            orderId: result.orderId ?? 0,
            order: result.order,
            product,
            funnelData
          });
          gtm.purchase({ orderId: result.orderId ?? 0, order: result.order, product, funnelData });
          blotout.funnelPurchase({ product, orderId: result.orderId, sku: variantSku });
          const siteId = process.env.NEXT_PUBLIC_SS_INDEX ?? '';
          const shopperId = getCookie('ssShopperId')?.toString() ?? '';
          Intellisuggest.OrderSale(siteId, shopperId, result.order, product);
          await customAnalyticsPromise;
        } finally {
          if (onSuccessUrl) {
            router.push(onSuccessUrl);
          }
        }
      } else if (result?.expired && onExpireUrl) {
        router.push(onExpireUrl);
        setError(true);
      } else if (result?.declined) {
        setDeclined(true);
        // Reset TILA modal state when payment is declined
        setShowTila(false);
        setNeedConfirm(false);
        if (result.checkoutId) {
          const checkoutUrl = createUrl(
            '/funnel-checkout',
            new URLSearchParams({
              cartId: result.checkoutId,
              productLine: productLine,
              funnelName: funnelName,
              declined: 'true',
              returnToOfferUrl,
              acceptOfferUrl,
              ancestor: result.ancestorId?.toString() ?? '',
              stepName,
              type,
              ...(state?.page?.queryParams ? { ...state.page.queryParams } : {})
            })
          );
          router.push(checkoutUrl);
        }
      } else {
        console.log(result?.message);
        setError(true);
        // Reset TILA modal state on other errors
        setShowTila(false);
        setNeedConfirm(false);
      }
    } catch (error) {
      console.log(error);
      setError(true);
      setPurchased(false);
      // Reset TILA modal state on any error
      setShowTila(false);
      setNeedConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ctaButtonRef.current && purchasedRef.current && containerRef.current) {
      const ctaButtonRect = ctaButtonRef.current.getBoundingClientRect();
      const purchasedRect = purchasedRef.current.getBoundingClientRect();

      const maxWidth = Math.max(ctaButtonRect.width, purchasedRect.width);
      const maxHeight = Math.max(ctaButtonRect.height, purchasedRect.height);

      containerRef.current.style.width = `${maxWidth}px`;
      containerRef.current.style.height = `${maxHeight}px`;
    }
  }, [isEditing]);

  return (
    <div
      ref={containerRef}
      style={attributes.style}
      className={`${attributes.className} click-to-purchase ${
        buttonColor === 'Text' ? 'click-to-purchase-text' : ''
      }`}
      builder-id={attributes['builder-id']}
      id={attributes.id}
    >
      <div ref={ctaButtonRef} className={`${!purchased ? 'visible' : ''}`}>
        <CTAButton
          buttonColor={buttonColor}
          buttonSize={buttonSize}
          buttonEmbellish={buttonEmbellish}
          buttonText={buttonText}
          buttonHTML={buttonHTML}
          loading={loading}
          buttonFontSize={buttonFontSize}
          onSubmit={onSubmit}
        />
        <div className={`tooltip ${declined ? 'visible' : ''}`}>Payment was declined...</div>
        <div className={`tooltip ${error ? 'visible' : ''}`}>Something went wrong...</div>
      </div>
      <div ref={purchasedRef} className={`${purchased ? 'visible' : ''}`}>
        <Purchased
          purchasedIcon={purchasedIcon}
          purchasedText={purchasedText}
          purchasedFontSize={purchasedFontSize}
          setPurchased={isEditing ? setPurchased : undefined}
        />
      </div>
      {isGuest && !isNewGuest && (
        <SignInModal
          isOpen={showAuthModal}
          onRequestClose={() => setShowAuthModal(false)}
          sku={variantSku}
          guestEmail={user?.email}
          quantity={1}
          productLine={productLine}
          funnelName={funnelName}
          returnToOfferUrl={`${returnToOfferUrl}?cancelSignInToPurchase=true`}
          acceptOfferUrl={acceptOfferUrl}
          stepName={stepName}
          type={type}
        />
      )}
      {user && isNewGuest && (
        <CreateAccountModal
          isOpen={showAuthModal}
          onRequestClose={() => setShowAuthModal(false)}
          user={user}
          onSuccess={() => {
            revalidateUser();
            setShowAuthModal(false);
            setShowTila(true);
          }}
        />
      )}
      {paymentPlan && (
        <TilaModal
          isOpen={showTila}
          onRequestClose={() => setShowTila(false)}
          onConfirm={handleConfirm}
          onSubmit={() => onSubmit(true)}
          sku={variantSku}
          onAfterClose={handleAfterClose}
        />
      )}
    </div>
  );
};

async function placeOTOOrder(sku: string): Promise<PlaceOTOOrderResponse | undefined> {
  const response = await fetch('/api/place-oto-order', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      sku,
      quantity: 1
    })
  });
  const result = await response.json();
  return result;
}

const ClickToPurchaseSkeleton = (
  <div className="click-to-purchase">
    <div className="visible">
      <button className="cta-button cta-button-atc min-w-56" disabled={true}>
        &nbsp;
      </button>
    </div>
  </div>
);

export const ClickToPurchaseCTA = withSuspense(
  ClickToPurchaseCTAComponent,
  ClickToPurchaseSkeleton
);
